import { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Bell, Megaphone, X, Check, AlertCircle, Calendar, DollarSign, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";

interface Notification {
  id: number;
  userId: number;
  weddingId?: number;
  type: string;
  emailSubject: string;
  emailBody: string;
  status: 'read' | 'unread';
  notificationDate: string;
  metadata?: any;
  createdAt: string;
}

interface Announcement {
  id: number;
  title: string;
  content: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'active' | 'inactive';
  createdAt: string;
  updatedAt: string;
}

export default function NotificationsPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("notifications");

  // Fetch notifications
  const { data: notifications = [], isLoading: notificationsLoading } = useQuery({
    queryKey: ['/api/notifications/client'],
    enabled: !!user,
    refetchInterval: 30000,
  });

  // Fetch announcements
  const { data: announcements = [], isLoading: announcementsLoading } = useQuery({
    queryKey: ['/api/announcements/active'],
    enabled: !!user,
    refetchInterval: 30000,
  });

  console.log('Announcements data:', announcements);
  console.log('Announcements array:', Array.isArray(announcements) ? announcements : []);
  console.log('Announcements length:', Array.isArray(announcements) ? announcements.length : 0);

  // Mark notification as read
  const markAsReadMutation = useMutation({
    mutationFn: (notificationId: number) => 
      apiRequest(`/api/notifications/${notificationId}/read`, {
        method: 'POST'
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/client'] });
      toast({
        title: "Notification marked as read",
        description: "The notification has been marked as read.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to mark notification as read.",
        variant: "destructive",
      });
    }
  });

  // Clear all notifications
  const clearAllMutation = useMutation({
    mutationFn: () => 
      apiRequest('/api/notifications/mark-all-read', {
        method: 'POST'
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/client'] });
      toast({
        title: "All notifications cleared",
        description: "All notifications have been marked as read.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to clear notifications.",
        variant: "destructive",
      });
    }
  });

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'stage_rejected':
        return <AlertCircle className="w-5 h-5 text-orange-500" />;
      case 'payment_reminder':
        return <DollarSign className="w-5 h-5 text-red-500" />;
      case 'event_approaching':
        return <Calendar className="w-5 h-5 text-blue-500" />;
      case 'booking_confirmed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'system_update':
        return <Bell className="w-5 h-5 text-purple-500" />;
      default:
        return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 dark:bg-red-900 border-red-300 dark:border-red-700';
      case 'high':
        return 'bg-orange-100 dark:bg-orange-900 border-orange-300 dark:border-orange-700';
      case 'medium':
        return 'bg-yellow-100 dark:bg-yellow-900 border-yellow-300 dark:border-yellow-700';
      case 'low':
        return 'bg-blue-100 dark:bg-blue-900 border-blue-300 dark:border-blue-700';
      default:
        return 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600';
    }
  };

  const unreadNotifications = Array.isArray(notifications) ? notifications.filter((n: Notification) => n.status === 'unread') : [];
  const unreadCount = unreadNotifications.length;
  const announcementCount = Array.isArray(announcements) ? announcements.length : 0;

  return (
    <div className="min-h-screen bg-black p-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-amber-400 mb-2">
            Notifications & Announcements
          </h1>
          <p className="text-amber-400/80">
            Stay updated with your wedding planning and system announcements
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6 bg-slate-800 border border-amber-500/20">
            <TabsTrigger value="notifications" className="relative text-amber-400 data-[state=active]:bg-amber-600 data-[state=active]:text-white">
              <Bell className="w-4 h-4 mr-2" />
              Notifications
              {unreadCount > 0 && (
                <Badge variant="destructive" className="ml-2 px-1 min-w-[20px] h-5 text-xs bg-red-600">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="announcements" className="relative text-amber-400 data-[state=active]:bg-amber-600 data-[state=active]:text-white">
              <Megaphone className="w-4 h-4 mr-2" />
              Announcements
              {announcementCount > 0 && (
                <Badge 
                  className="ml-2 px-1 min-w-[20px] h-5 text-xs bg-amber-600 border-amber-500 text-white"
                >
                  {announcementCount > 99 ? '99+' : announcementCount}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="notifications" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-amber-400">
                Your Notifications
              </h2>
              {unreadCount > 0 && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => clearAllMutation.mutate()}
                  disabled={clearAllMutation.isPending}
                  className="text-amber-400 border-amber-500/30 hover:bg-amber-500/10 hover:text-amber-300"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Clear All
                </Button>
              )}
            </div>

            {notificationsLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="animate-pulse">
                    <CardContent className="p-4">
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : Array.isArray(notifications) && notifications.length === 0 ? (
              <Card className="bg-slate-800 border-amber-500/20">
                <CardContent className="text-center py-8">
                  <Bell className="w-12 h-12 text-amber-500/30 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-amber-400 mb-2">
                    No notifications
                  </h3>
                  <p className="text-amber-400/60">
                    You're all caught up! We'll notify you when there's something new.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {Array.isArray(notifications) && notifications.map((notification: Notification) => {
                  // metadata is already an object from the database (jsonb), no need to parse
                  const metadata = notification.metadata || {};

                  return (
                    <Card 
                      key={notification.id}
                      className={`${notification.status === 'unread' 
                        ? 'border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20' 
                        : 'border-gray-200 dark:border-gray-700'
                      } transition-all hover:shadow-md`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3 flex-1">
                            <div className="flex-shrink-0">
                              {getNotificationIcon(notification.type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                                {notification.emailSubject}
                              </h3>
                              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                {notification.emailBody}
                              </p>
                              <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                                <span>
                                  {notification.notificationDate && !isNaN(new Date(notification.notificationDate).getTime()) 
                                    ? formatDistanceToNow(new Date(notification.notificationDate), { addSuffix: true })
                                    : 'recently'}
                                </span>
                                {notification.status === 'unread' && (
                                  <Badge variant="secondary" className="text-xs">
                                    New
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {notification.status === 'unread' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => markAsReadMutation.mutate(notification.id)}
                                disabled={markAsReadMutation.isPending}
                                className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
                              >
                                <Check className="w-4 h-4 mr-1" />
                                Mark Read
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => markAsReadMutation.mutate(notification.id)}
                              disabled={markAsReadMutation.isPending}
                              className="text-slate-400 hover:text-amber-400"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="announcements" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-amber-400">
                System Announcements
              </h2>
            </div>

            {announcementsLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="animate-pulse bg-slate-800 border-amber-500/20">
                    <CardContent className="p-4">
                      <div className="h-4 bg-amber-500/30 rounded mb-2"></div>
                      <div className="h-3 bg-amber-500/20 rounded w-3/4"></div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : Array.isArray(announcements) && announcements.length === 0 ? (
              <Card className="bg-slate-800 border-amber-500/20">
                <CardContent className="text-center py-8">
                  <Megaphone className="w-12 h-12 text-amber-500/30 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-amber-400 mb-2">
                    No announcements
                  </h3>
                  <p className="text-amber-400/60">
                    There are no active system announcements at the moment.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {Array.isArray(announcements) && announcements.map((announcement: Announcement) => (
                  <Card 
                    key={announcement.id}
                    className={`${getPriorityColor(announcement.priority)} transition-all hover:shadow-md bg-slate-800 border-amber-500/20`}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg text-amber-400">
                          {announcement.title}
                        </CardTitle>
                        <Badge 
                          variant="outline"
                          className={`
                            ${announcement.priority === 'urgent' ? 'border-red-600 text-red-600' : ''}
                            ${announcement.priority === 'high' ? 'border-amber-600 text-amber-600' : ''}
                            ${announcement.priority === 'medium' ? 'border-amber-500 text-amber-500' : ''}
                            ${announcement.priority === 'low' ? 'border-amber-400 text-amber-400' : ''}
                          `}
                        >
                          {announcement.priority.charAt(0).toUpperCase() + announcement.priority.slice(1)}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-amber-400/80 mb-3 whitespace-pre-wrap">
                        {announcement.content}
                      </p>
                      <div className="text-xs text-amber-400/50">
                        Posted {announcement.createdAt && !isNaN(new Date(announcement.createdAt).getTime()) 
                          ? formatDistanceToNow(new Date(announcement.createdAt), { addSuffix: true })
                          : 'recently'}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}